import React, { useState } from 'react';

export interface PeekStyle {
  /** Slot CSS transform: scale factor. Default: 1. */
  scale?: number;
  /** Slot opacity 0–1. Default: 1. */
  opacity?: number;
}

interface PeekSnapCarouselProps {
  /**
   * Width of each card slot — any CSS length (`'18rem'`, `'248px'`, `'80vw'`).
   * Drives the slot width AND the symmetric side padding
   * `calc((100% - cardWidth) / 2)` that allows every card to snap to viewport centre.
   */
  cardWidth: string;
  /** Tailwind gap class between cards. */
  gap?: string;
  /** Extra classes for the scroll viewport. */
  className?: string;
  /** Style applied to the active (centred) slot. */
  activePeek?: PeekStyle;
  /** Style applied to inactive (off-centre) slots. */
  inactivePeek?: PeekStyle;
  /** Transition duration in ms for the active/inactive swap. */
  transitionDurationMs?: number;
  /** Fired whenever the centred index changes. */
  onActiveChange?: (index: number) => void;
  /**
   * Cards to render. Each child is wrapped in a slot that owns snap behaviour
   * AND the active/inactive transition. Card styles are NOT touched — let cards
   * use `w-full` to fill the slot so they resize with `cardWidth`.
   */
  children: React.ReactNode;
}

// Default config — tweak here, not per-call.
const DEFAULT_GAP = 'gap-2';
const DEFAULT_TRANSITION_MS = 300;
const DEFAULT_ACTIVE_PEEK: Required<PeekStyle> = { scale: 1, opacity: 1 };
const DEFAULT_INACTIVE_PEEK: Required<PeekStyle> = { scale: 0.9, opacity: 0.6 };
// Tailwind arbitrary variant — hides the webkit scrollbar without a global stylesheet.
const HIDE_SCROLLBAR_CLASS = '[&::-webkit-scrollbar]:hidden';

export const PeekSnapCarousel: React.FC<PeekSnapCarouselProps> = ({
  cardWidth,
  gap = DEFAULT_GAP,
  className = '',
  activePeek,
  inactivePeek,
  transitionDurationMs = DEFAULT_TRANSITION_MS,
  onActiveChange,
  children,
}) => {
  const [active, setActive] = useState(0);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const container = e.currentTarget;
    const center = container.scrollLeft + container.offsetWidth / 2;
    const slots = Array.from(container.children) as HTMLElement[];

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    slots.forEach((slot, i) => {
      const slotCenter = slot.offsetLeft + slot.offsetWidth / 2;
      const distance = Math.abs(center - slotCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    if (closestIndex !== active) {
      setActive(closestIndex);
      onActiveChange?.(closestIndex);
    }
  };

  const resolvedActive: Required<PeekStyle> = {
    ...DEFAULT_ACTIVE_PEEK,
    ...activePeek,
  };
  const resolvedInactive: Required<PeekStyle> = {
    ...DEFAULT_INACTIVE_PEEK,
    ...inactivePeek,
  };
  const transition = `transform ${transitionDurationMs}ms ease, opacity ${transitionDurationMs}ms ease`;

  return (
    <div
      onScroll={handleScroll}
      className={`flex ${gap} overflow-x-auto snap-x snap-mandatory scroll-smooth ${HIDE_SCROLLBAR_CLASS} ${className}`}
      style={{
        paddingLeft: `calc((100% - ${cardWidth}) / 2)`,
        paddingRight: `calc((100% - ${cardWidth}) / 2)`,
        scrollbarWidth: 'none',
      }}
    >
      {React.Children.map(children, (child, index) => {
        const peek = index === active ? resolvedActive : resolvedInactive;
        return (
          <div
            className="snap-center [scroll-snap-stop:always]"
            style={{
              flex: `0 0 ${cardWidth}`,
              width: cardWidth,
              transform: `scale(${peek.scale})`,
              opacity: peek.opacity,
              transition,
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};
